/**
 * The app's single Apollo client.
 *
 * One HTTP link for queries and mutations (upload-capable, bearer token from
 * localStorage) and one lazy WebSocket link for subscriptions, split on the
 * operation kind. The ngrok-skip header is kept so a tunnel can stand in for
 * the backend without tripping ngrok's browser interstitial.
 */
import { ApolloClient, InMemoryCache, from, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import createUploadLink from 'apollo-upload-client/public/createUploadLink.js';

import { API_URL, GRAPHQL_WS_URL } from './config/backend';
import { isAuthFailure } from './lib/authFailure';

// Upload-capable HTTP transport.
const uploadLink = createUploadLink({
  uri: API_URL,
});

// Bearer token on every HTTP request.
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
  };
});

/* ── the session going stale ──────────────────────────────────────────────
 *
 * An expired or revoked token does NOT arrive as an HTTP 401 — the server
 * answers 200 with a GraphQL error reading "403: Authentication required" — so
 * a link watching `networkError.statusCode` would never see it, which is why
 * the app used to sit on a dead session showing an error on every card.
 *
 * What counts as a dead session lives in lib/authFailure.js, on its own and
 * under test: matching too widely would throw people out mid-task for asking
 * a question they were not allowed to ask.
 */
let authFailureHandler = null;
/** Register what happens when the session dies. Returns the unsubscribe. */
export const onAuthFailure = (fn) => {
  authFailureHandler = fn;
  return () => { if (authFailureHandler === fn) authFailureHandler = null; };
};

/* Every in-flight query fails at once when a token dies, so the handler is
   called once and then muted until somebody signs in again. */
let reported = false;
export const resetAuthFailure = () => { reported = false; };

const authErrorLink = onError((payload) => {
  if (!isAuthFailure(payload)) return;

  /* Nothing to end if no session is stored: an unauthenticated call on the
     login screen is not an expiry. */
  let hasToken = false;
  try {
    hasToken = Boolean(localStorage.getItem('authToken'));
  } catch {
    hasToken = false;
  }
  if (!hasToken || reported) return;

  reported = true;
  authFailureHandler?.();
});

// WebSocket link for GraphQL subscriptions.
const wsLink = new GraphQLWsLink(
  createClient({
    url: GRAPHQL_WS_URL,
    lazy: true,
    retryAttempts: 3,
    connectionParams: () => {
      const token = localStorage.getItem('authToken');
      return {
        Authorization: token ? `Bearer ${token}` : '',
        authorization: token ? `Bearer ${token}` : '',
      };
    },
    on: {
      error: (err) => {
        console.warn('[WS] Connection error:', err);
      },
      closed: (event) => {
        if (event && event.code !== 1000) {
          console.warn('[WS] Connection closed unexpectedly:', event.code, event.reason);
        }
      },
    },
  })
);

// Subscriptions → WS, everything else → HTTP.
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([authErrorLink, authLink, uploadLink]),
);

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({ addTypename: false }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
    query: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
